// =========================================================================
// Helper de normalisation universelle (miroir client_matching backend).
// Règles : metier_config_listes.normalisation_types (chargées via API)
// Préfixes : metier_config_listes.prefixes_ignores_matching
// =========================================================================
//
// Usage typique sur les inputs :
//   <Input
//     value={form.nom}
//     onChange={(e) => setForm({...form, nom: e.target.value})}
//     onBlur={(e) => setForm({...form, nom: normalizeName(e.target.value, "nom")})}
//   />
//
// Backend Pydantic (ClientCreate) refait la normalisation au save —
// le onBlur est cosmétique (feedback visuel immédiat à l'utilisateur).

const DEFAULT_TYPE_RULE = {
  nom:        "UPPER_NOACCENT_TRIM",
  prenom:     "TITLE_LOWER",
  entreprise: "UPPER_NOACCENT_TRIM",
  ville:      "TITLE_LOWER",
  adresse:    "TITLE_FIRST_ONLY",
};

const DEFAULT_PREFIXES = [
  "SAS", "SARL", "SA", "SASU", "SNC", "EURL",
  "GROUPE", "GROUP",
  "MME", "M", "MR", "MADAME", "MONSIEUR",
  "THE", "LES", "LE", "LA", "L'",
];

function stripAccents(s) {
  return s.normalize("NFKD").replace(/[̀-ͯ]/g, "");
}

function titleCase(s) {
  if (!s) return "";
  const out = [];
  for (const word of s.split(/(\s+)/)) {
    if (!word || /^\s+$/.test(word)) { out.push(word); continue; }
    // Acronyme court ALL-CAPS → garder
    if (word.length <= 3 && word === word.toUpperCase() && /^[A-Z]+$/.test(word)) {
      out.push(word);
      continue;
    }
    const lowered = word.toLowerCase();
    out.push(lowered.split(/([-'’])/).map((p) =>
      p && /^[a-zà-ÿ]/i.test(p[0]) ? p[0].toUpperCase() + p.slice(1) : p
    ).join(""));
  }
  return out.join("").trim();
}

function dropPrefixes(s, prefixes = DEFAULT_PREFIXES) {
  let out = s;
  let changed = true;
  while (changed) {
    changed = false;
    for (const p of prefixes) {
      const P = p.toUpperCase();
      for (const sep of [" ", "-", "."]) {
        if (out.startsWith(P + sep)) {
          out = out.slice(P.length + 1).trim();
          changed = true;
          break;
        }
      }
      if (changed) break;
      if (P.endsWith("'") && out.startsWith(P)) {
        out = out.slice(P.length).trim();
        changed = true;
        break;
      }
    }
  }
  return out;
}

const RULES = {
  UPPER_NOACCENT_TRIM:      (s) => stripAccents(s).toUpperCase().trim(),
  TITLE_LOWER:              (s) => titleCase(s.trim()),
  UPPER_NOACCENT_NOPREFIX:  (s, pfx) => dropPrefixes(stripAccents(s).toUpperCase().trim(), pfx),
  TITLE_FIRST_ONLY:         (s) => {
    const t = s.trim();
    return t ? t[0].toUpperCase() + t.slice(1).toLowerCase() : "";
  },
};

/**
 * Normalise une valeur selon le type métier.
 * @param {string|null|undefined} value
 * @param {string} type "nom" | "prenom" | "entreprise" | "ville" | "adresse"
 * @param {object} [options]
 * @param {object} [options.ruleOverrides]
 * @param {string[]} [options.prefixes]
 * @returns {string}
 */
export function normalizeName(value, type, options = {}) {
  if (value == null || value === "") return "";
  const s = String(value);
  const rules = { ...DEFAULT_TYPE_RULE, ...(options.ruleOverrides || {}) };
  const code = rules[type] || type;
  const fn = RULES[code];
  if (!fn) return s.trim();
  return fn(s, options.prefixes || DEFAULT_PREFIXES);
}

/**
 * Comparaison fuzzy (UPPER, sans accents, préfixes droppés pour entreprise).
 */
export function normalizeForMatching(value, type = "nom", options = {}) {
  if (value == null || value === "") return "";
  let base = stripAccents(String(value)).toUpperCase().trim().replace(/\s+/g, " ");
  if (type === "entreprise" || type === "raison_sociale") {
    return dropPrefixes(base, options.prefixes || DEFAULT_PREFIXES);
  }
  return base;
}

/**
 * Charge la config DB une fois et la cache.
 * Appelé idéalement au boot de l'app.
 */
let _cachedConfig = null;
export async function loadNormalizeConfig(api, getHeaders) {
  try {
    const [typesRes, prefixesRes] = await Promise.all([
      fetch(`${api}/api/metier/config-listes/normalisation_types`, { headers: getHeaders(), credentials: "include" }),
      fetch(`${api}/api/metier/config-listes/prefixes_ignores_matching`, { headers: getHeaders(), credentials: "include" }),
    ]);
    const typesItems = typesRes.ok ? (await typesRes.json()).items || [] : [];
    const prefixesItems = prefixesRes.ok ? (await prefixesRes.json()).items || [] : [];
    const ruleMap = { ...DEFAULT_TYPE_RULE };
    for (const it of typesItems) {
      if (it.id && it.rule) ruleMap[it.id] = it.rule;
    }
    const prefixes = prefixesItems
      .map((it) => (it.id || it.label || "").toUpperCase())
      .filter(Boolean);
    _cachedConfig = { types: ruleMap, prefixes: prefixes.length ? prefixes : DEFAULT_PREFIXES };
    return _cachedConfig;
  } catch {
    return { types: DEFAULT_TYPE_RULE, prefixes: DEFAULT_PREFIXES };
  }
}

export function getCachedConfig() {
  return _cachedConfig || { types: DEFAULT_TYPE_RULE, prefixes: DEFAULT_PREFIXES };
}

// =========================================================================
// NORMALISATION DES IDENTIFIANTS (incident #314, volet structurel)
// =========================================================================
//
// MOTIF
//   Le SIRET « 940 455 132 00016 » collé depuis un Kbis fait 18 caractères ;
//   le backend impose `max_length=14` → 422 → page blanche (#314).
//   La même famille de défaut guette SIREN, TVA, IBAN et téléphone : tous
//   sont affichés par groupes chez leurs émetteurs et collés avec séparateurs.
//
// PRINCIPE — un point unique par TYPE de donnée, pas une correction par champ.
//   Ajouter un identifiant = ajouter une ligne dans IDENTIFIER_RULES.
//   Aucun champ ne réimplémente sa propre règle.
//
// CONTRAT
//   La normalisation est CONSERVATRICE : elle retire des séparateurs et
//   tronque à la longueur légale. Elle ne valide pas (pas de clé de Luhn,
//   pas de contrôle IBAN) — la validation reste au backend, qui fait foi.

const IDENTIFIER_RULES = {
  // SIRET : 14 chiffres. Affiché « 940 455 132 00016 ».
  siret:      { garder: /[0-9]/g, longueurMax: 14 },
  // SIREN : 9 chiffres. Affiché « 940 455 132 ».
  siren:      { garder: /[0-9]/g, longueurMax: 9 },
  // TVA intracom FR : « FR » + clé 2 + SIREN 9 = 13. Affiché « FR 12 345678901 ».
  tva:        { garder: /[0-9A-Z]/g, longueurMax: 13, majuscules: true },
  // IBAN : 34 max (norme ISO 13616). Affiché par groupes de 4.
  iban:       { garder: /[0-9A-Z]/g, longueurMax: 34, majuscules: true },
  // Téléphone : E.164, 15 chiffres max, « + » initial conservé.
  telephone:  { garder: /[0-9]/g, longueurMax: 15, plusInitial: true },
};

/** Alias tolérés — un même type nommé différemment selon les écrans. */
const IDENTIFIER_ALIASES = {
  siret_entreprise: "siret",
  numero_siret: "siret",
  numero_siren: "siren",
  tva_intracommunautaire: "tva",
  tva_intra: "tva",
  numero_tva: "tva",
  telephone_secondaire: "telephone",
  tel: "telephone",
  mobile: "telephone",
};

/**
 * Normalise un identifiant à la saisie : retire les séparateurs, met en
 * majuscules si le type l'exige, tronque à la longueur légale.
 *
 * Sûre par construction : un type inconnu renvoie la valeur seulement trimée,
 * jamais une chaîne vide — on ne fait pas disparaître une saisie.
 *
 * @param {string|null|undefined} value
 * @param {"siret"|"siren"|"tva"|"iban"|"telephone"|string} type
 * @returns {string}
 *
 * @example
 * normalizeIdentifier("940 455 132 00016", "siret") // → "94045513200016"
 * normalizeIdentifier("FR 12 345678901", "tva")     // → "FR12345678901"
 * normalizeIdentifier("+33 6 12 34 56 78", "telephone") // → "+33612345678"
 */
export function normalizeIdentifier(value, type) {
  if (value == null || value === "") return "";
  const brut = String(value);
  const cle = IDENTIFIER_ALIASES[type] || type;
  const regle = IDENTIFIER_RULES[cle];
  if (!regle) return brut.trim();

  const source = regle.majuscules ? brut.toUpperCase() : brut;
  const plus = regle.plusInitial && brut.trim().startsWith("+") ? "+" : "";
  const retenus = source.match(regle.garder) || [];
  return plus + retenus.join("").slice(0, regle.longueurMax);
}

/** Types d'identifiants gérés — pour les tests et l'introspection. */
export function identifierTypes() {
  return Object.keys(IDENTIFIER_RULES);
}
